import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AeonyxWordmark } from "@/components/aeonyx/Logo";
import { toast } from "sonner";
import {
  Gauge,
  Package,
  ShoppingCart,
  Users,
  Tag,
  Ticket,
  ChartLineUp,
  Star,
  ChatCircleText,
  Gear,
  SignOut,
  ArrowSquareOut,
  Article,
  Question,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: Gauge },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/blogs", label: "Blogs", icon: Article },
  { to: "/admin/faqs", label: "FAQs", icon: Question },
  { to: "/admin/categories", label: "Categories", icon: Tag },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/analytics", label: "Analytics", icon: ChartLineUp },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/messages", label: "Messages", icon: ChatCircleText },
  { to: "/admin/settings", label: "Settings", icon: Gear },
];

export default function AdminLayout({ children, title }) {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const displayName =
    profile?.full_name || user?.user_metadata?.full_name || user?.email?.split("@")[0];

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Sign out failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-navy-deep text-white flex-col z-30"
      >
        <div className="px-5 py-6 border-b border-white/10">
          <NavLink to="/" aria-label="Home" className="inline-flex">
            <AeonyxWordmark className="h-8 w-auto brightness-0 invert" priority />
          </NavLink>
          <div className="mt-4 flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full bg-cta-gradient grid place-items-center text-white font-display font-bold text-sm uppercase">
              {displayName?.[0] || "A"}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold truncate">{displayName}</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-teal-brand font-mono-data font-semibold">
                Admin
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] transition-colors ${
                  isActive
                    ? "bg-white/10 text-white font-semibold"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
              end={n.to === "/admin/dashboard"}
            >
              <n.icon size={17} weight="regular" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          <NavLink
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-white/70 hover:text-white hover:bg-white/5"
          >
            <ArrowSquareOut size={16} weight="regular" /> View storefront
          </NavLink>
          <button
            onClick={handleSignOut}
            data-testid="admin-signout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-white/70 hover:text-white hover:bg-white/5"
          >
            <SignOut size={16} weight="regular" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:pl-64">
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-slate-200">
          <div className="flex items-center justify-between px-5 lg:px-10 py-4">
            <div>
              <div className="text-[10.5px] font-mono-data uppercase tracking-[0.14em] text-cyan-brand font-semibold">
                Admin
              </div>
              <h1 className="font-display text-xl md:text-2xl font-bold text-navy-deep tracking-tight leading-tight">
                {title}
              </h1>
            </div>
            <div className="hidden md:flex items-center gap-2 text-[12px] text-slate-500 font-mono-data">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Signed in as {user?.email}
            </div>
          </div>
        </header>

        <main className="px-5 lg:px-10 py-6 lg:py-10">{children}</main>
      </div>
    </div>
  );
}
